// ISIN to Stock Code Conversion Test Script
// Testing OpenFIGI API for US, European, Canadian, and Swiss markets

export interface TestCase {
  isin: string;
  market: string;
  expectedTicker?: string;
  description: string;
}

// Test cases covering all target markets
const TEST_ISINS: TestCase[] = [
  // US Stocks
  { isin: 'US0378331005', market: 'US', expectedTicker: 'AAPL', description: 'Apple Inc. (US)' },
  { isin: 'US5949181045', market: 'US', expectedTicker: 'MSFT', description: 'Microsoft Corp (US)' },
  
  // Swiss Stocks (SIX Exchange)
  { isin: 'CH0012005267', market: 'CH', expectedTicker: 'NESN.SW', description: 'Nestle SA (Swiss)' },
  { isin: 'CH0244767585', market: 'CH', expectedTicker: 'NOVN.SW', description: 'Novartis AG (Swiss)' },
  { isin: 'CH0038863350', market: 'CH', expectedTicker: 'ROG.SW', description: 'Roche Holding AG (Swiss)' },
  
  // European Stocks
  { isin: 'DE0007164600', market: 'DE', expectedTicker: 'SAP.DE', description: 'SAP SE (Germany)' },
  { isin: 'FR0000120271', market: 'FR', expectedTicker: 'TTE.PA', description: 'TotalEnergies (France)' },
  { isin: 'NL0011540547', market: 'NL', expectedTicker: 'ASML.AS', description: 'ASML Holding (Netherlands)' },
  
  // Canadian Stocks
  { isin: 'CA89417E1055', market: 'CA', expectedTicker: 'TD.TO', description: 'TD Bank (Canada)' },
  { isin: 'CA7800871021', market: 'CA', expectedTicker: 'RY.TO', description: 'Royal Bank of Canada' },
];

interface OpenFIGIRequest {
  idType: string;
  idValue: string;
  exchCode?: string;
}

interface OpenFIGIResponse {
  data?: Array<{
    figi: string;
    ticker: string;
    name: string;
    exchCode: string;
    compositeFIGI: string;
    securityType: string;
    marketSector: string;
  }>;
  error?: string;
}

// OpenFIGI API endpoint (free, no API key required for basic usage)
const OPENFIGI_API_URL = 'https://api.openfigi.com/v3/mapping';

async function testISINConversion(testCase: TestCase): Promise<{
  success: boolean;
  ticker: string;
  name: string;
  exchange: string;
  error?: string;
}> {
  try {
    const requestBody: OpenFIGIRequest[] = [{
      idType: 'ID_ISIN',
      idValue: testCase.isin
    }];

    const response = await fetch(OPENFIGI_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`OpenFIGI API returned ${response.status}`);
    }

    const data: OpenFIGIResponse[] = await response.json();
    
    if (data && data[0] && data[0].data && data[0].data.length > 0) {
      // Find the most relevant result (prefer equity/common stock)
      const equityResult = data[0].data.find(d => 
        d.securityType === 'Common Stock' || d.marketSector === 'Equity'
      ) || data[0].data[0];

      return {
        success: true,
        ticker: equityResult.ticker,
        name: equityResult.name,
        exchange: equityResult.exchCode
      };
    }

    return {
      success: false,
      ticker: '',
      name: '',
      exchange: '',
      error: data[0]?.error || 'No results found'
    };

  } catch (error) {
    return {
      success: false,
      ticker: '',
      name: '',
      exchange: '',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Run all tests and return results
export async function runISINConversionTests(): Promise<void> {
  console.log('🧪 ISIN to Stock Code Conversion Test\n');
  console.log('Testing OpenFIGI API for US, European, Canadian, and Swiss markets\n');
  console.log('═'.repeat(80));

  let successCount = 0;
  let failureCount = 0;

  for (const testCase of TEST_ISINS) {
    console.log(`\n📋 Testing: ${testCase.description}`);
    console.log(`   ISIN: ${testCase.isin}`);
    console.log(`   Market: ${testCase.market}`);
    
    const result = await testISINConversion(testCase);
    
    if (result.success) {
      console.log(`   ✅ SUCCESS`);
      console.log(`   Ticker: ${result.ticker}`);
      console.log(`   Name: ${result.name}`);
      console.log(`   Exchange: ${result.exchange}`);
      
      if (testCase.expectedTicker && result.ticker !== testCase.expectedTicker) {
        console.log(`   ⚠️  Note: Got ${result.ticker}, expected ${testCase.expectedTicker}`);
      }
      
      successCount++;
    } else {
      console.log(`   ❌ FAILED: ${result.error}`);
      failureCount++;
    }
    
    // Rate limiting: wait 250ms between requests
    await new Promise(resolve => setTimeout(resolve, 250));
  }

  console.log('\n' + '═'.repeat(80));
  console.log(`\n📊 Test Results:`);
  console.log(`   ✅ Passed: ${successCount}/${TEST_ISINS.length}`);
  console.log(`   ❌ Failed: ${failureCount}/${TEST_ISINS.length}`);
  console.log(`   Success Rate: ${((successCount / TEST_ISINS.length) * 100).toFixed(1)}%`);
  
  if (successCount === TEST_ISINS.length) {
    console.log('\n✨ All tests passed! OpenFIGI API is working correctly for all markets.');
  } else if (successCount >= TEST_ISINS.length * 0.8) {
    console.log('\n⚠️  Most tests passed. OpenFIGI API is working but some ISINs may need fallback.');
  } else {
    console.log('\n❌ Too many failures. Consider alternative approach or check API availability.');
  }
}

// Export for use in browser console
if (typeof window !== 'undefined') {
  (window as any).runISINTests = runISINConversionTests;
}
